import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../../components/UI/Layouts";
import Content from "../../components/UI/Layouts/Content";
import { Banner, EditBanner } from "../../types";
import PageHeader from "../../components/common/PageHeader";
import { useTranslation } from "react-i18next";
import { useStores } from "../../hooks/useStores";
import { VirtualizedTable } from "../../components/common/VirtualizedTable";
import { Breadcrumb, BreadcrumbItem } from "../../components/UI/Breadcrumb";
import { useSnackbar } from "notistack";
import { observer } from "mobx-react";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import PopupState, { bindTrigger, bindPopover } from "material-ui-popup-state";
import DeleteIcon from "@mui/icons-material/Delete";
import EmptySpace from "../../components/common/EmptySpace";
import Loading from "../../components/common/Loading";
import BannerModal from "../../components/ScreenBanners/BannerModal";
import EditIcon from "@mui/icons-material/Edit";
import moment from "moment";

const Banners: React.FC = observer(() => {
  const { bannerStore } = useStores();
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [banner, setBanner] = useState<Banner>();
  const modalHandler = () => {
    setIsModalOpen((open) => !open);
  };
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    setIsLoading(true);
    bannerStore
      .getBanners()
      .then(() => setIsLoading(false))
      .catch((err: any) => {
        enqueueSnackbar(err.message, {
          variant: "error",
        });
      });
  }, []);

  const handleSubmit = useCallback(
    async (data: EditBanner) => {
      setIsLoading(true);
      try {
        await bannerStore.addBanner(data).then(() => {
          setIsLoading(false);
        });
        enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
          variant: "success",
        });
      } catch (error: any) {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      }

      modalHandler();
    },
    [bannerStore, enqueueSnackbar, t]
  );

  const handleUpdate = useCallback(
    async (data: EditBanner) => {
      setIsLoading(true);
      try {
        await bannerStore.updateBanner(data).then(() => {
          setIsLoading(false);
        });
        enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
          variant: "success",
        });
      } catch (error: any) {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      }
      setBanner(undefined);
      modalHandler();
    },
    [bannerStore, enqueueSnackbar, t]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      setIsLoading(true);

      try {
        await bannerStore.deleteBanner(id).then(() => {
          setIsLoading(false);
        });
        enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
          variant: "success",
        });
      } catch (error: any) {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      }
    },
    [bannerStore, enqueueSnackbar, t]
  );

  const columns = useMemo(
    () => [
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.banners.column.title"),
        dataKey: "title",
        render: (title: string) => title,
      },
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.banners.column.validFrom"),
        dataKey: "validFrom",
        render: (validFrom: Date) => moment(validFrom).format("DD/MM/YYYY"),
      },
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.banners.column.validUntil"),
        dataKey: "validUntil",
        render: (validUntil: Date) => moment(validUntil).format("DD/MM/YYYY"),
      },
      {
        width: 90,
        flexGrow: 0,
        label: t("common.action"),
        dataKey: "id",
        render: (id: string, banner: Banner) => {
          return (
            <>
              <IconButton
                key={id}
                onClick={() => {
                  setBanner(banner);
                  modalHandler();
                }}
              >
                <EditIcon />
              </IconButton>
              <PopupState variant="popover" popupId="demo-popup-popover">
                {(popupState) => (
                  <div>
                    <IconButton color="error" {...bindTrigger(popupState)}>
                      <DeleteIcon />
                    </IconButton>
                    <Popover
                      {...bindPopover(popupState)}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "center",
                      }}
                    >
                      <Typography sx={{ p: 1 }}>
                        {t("common.deletePopUp")}
                        <IconButton
                          color="error"
                          sx={{ p: 0 }}
                          onClick={() => handleDelete(id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Typography>
                    </Popover>
                  </div>
                )}
              </PopupState>
            </>
          );
        },
      },
    ],
    [handleDelete, t]
  );

  return (
    <Layout hideFooter>
      <Content borderBottomWidth="1px" align="top">
        <Breadcrumb my={"20px"}>
          <BreadcrumbItem path="/">
            {t("screens.banners.breadcrumb.home")}
          </BreadcrumbItem>
          <BreadcrumbItem active>
            {t("screens.banners.breadcrumb.banners")}
          </BreadcrumbItem>
        </Breadcrumb>

        <PageHeader
          title={t("screens.banners.pageHeader.title")}
          buttonEnable={true}
          buttonText={t("screens.banners.pageHeader.buttonText")}
          handleButton={modalHandler}
        />
      </Content>
      {isLoading ? (
        <Loading />
      ) : !isLoading && bannerStore.banners.length > 0 ? (
        <Content fullHeight align="top" mt={[null, null, null, "0px"]}>
          <div
            style={{
              flex: "1 1 auto",
              width: "100%",
              height: `${(bannerStore.banners.length + 1) * 50}px`,
              display: "flex",
              marginTop: "20px",
            }}
          >
            <VirtualizedTable
              rowCount={bannerStore.banners.length}
              rowGetter={({ index }) => bannerStore.banners[index]}
              columns={columns}
              rowStyle={(row) =>
                row.index % 2 === 0 ? { background: "#EDF1FB" } : {}
              }
            />
          </div>
        </Content>
      ) : (
        <EmptySpace
          title={t("screens.banners.emptyData")}
          buttonEnable={true}
          buttonText={t("screens.banners.pageHeader.buttonText")}
          handleButton={modalHandler}
        />
      )}

      <BannerModal
        banner={banner}
        isSubmitting={false}
        isOpen={isModalOpen}
        onClose={modalHandler}
        onSubmit={banner ? handleUpdate : handleSubmit}
      />
    </Layout>
  );
});

export default Banners;
